import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { useHostConfigurationSaveMutation } from "../../services/server/HostConfigurationApi";
import type { HostConfigurationEntity } from "../../types/HostConfigurationEntity";

export const HostConfigurationCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("host-configuration");

    const [formData, setFormData] = useState<HostConfigurationEntity>({} as HostConfigurationEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useHostConfigurationSaveMutation();
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.HOST_CONFIGURATIONS}`);
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", {
                        id: result.data.id,
                        resource: "Host Configuration",
                    }),
                    `${BASE_PATH}/${ResourceName.HOST_CONFIGURATIONS}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<HostConfigurationEntity>
                    name="HostXmlConfigurationCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
